import React, { useState, useEffect, useMemo } from "react";
import { Button, Dropdown, Checkbox, Tooltip } from "antd";
import "./calculateTask3LG.less";
import dayjs from "dayjs";
import { replaceFormulaStr } from "@/config/func";
import { rebuildFormula } from "@/config/func";
import { useKeyComponents } from "../ResultPageFrame";
import { formatBySig, resolveSig, formatErrorPercent, isNumValue } from "@/utils/numberFormat";

type DataMode = "massFlow" | "massFraction" | "molarFlow" | "massConcentration" | "moleFrac";

interface Props {
  streams: any[];
  input: any;
  compareItems: any[];
  showAllData?: boolean;
  streamDescMap?: Record<string, string>;
  streamNodeMap?: Record<string, { fromName: string; toName: string }>;
  elementDescMap?: Record<string, string>;
}

const ResultComponentTable: React.FC<Props> = ({ streams, input, compareItems, showAllData = true, streamDescMap = {}, streamNodeMap = {}, elementDescMap = {} }) => {
  const [mode, setMode] = useState<DataMode>("moleFrac");
  const [materials, setMaterials] = useState<any[]>([]);
  const { keyComponents, setKeyComponents, keyComponentsEverSet } = useKeyComponents();

  const modes = [
    { value: "moleFrac", label: "摩尔分率" },
    { value: "massFraction", label: "质量分率" },
    { value: "massFlow", label: "质量流量（kg/h）" },
    { value: "molarFlow", label: "摩尔流量（kmol/h）" },
    { value: "massConcentration", label: "质量浓度（mg/Nm³）" },
  ];

  useEffect(() => {
    if (input?.inputs?.streams) {
      const m = input.inputs.streams.find((item: any) => item.materials?.length);
      const mats = m?.materials ?? [];
      setMaterials(mats);
      // 默认关键组分：有甲醇或水则仅勾选甲醇/水，否则全选（仅在首次初始化）
      if (keyComponents.length === 0 && mats.length > 0 && !keyComponentsEverSet.current) {
        const allLabels = mats.map((mat: any) => rebuildFormula(mat?.name || ""));
        //甲醇水
        // const waterMethanolLabels = mats
        //   .filter((mat: any) => {
        //     const name = mat?.name || "";
        //     return name.includes("CH3OH") || name.includes("H2O");
        //   })
        //   .map((mat: any) => rebuildFormula(mat.name));
        // const initial = waterMethanolLabels.length > 0 ? waterMethanolLabels : allLabels;
        // setKeyComponents(initial);
        setKeyComponents(allLabels);
        keyComponentsEverSet.current = true;
      }
    }
  }, [input, keyComponents.length, setKeyComponents]);

  // 格式化数值：原数据是什么就显示什么，仅处理无效值
  const fmt = (v: any, fixed = 14) => {
    if (v == null || isNaN(Number(v))) return "—";
    return v;
  };

  // 计算误差百分比：0 显示 0%，常规差异显示 x.xx%，非 0 但极小显示 <0.01%
  const calculateErrorPercent = (compareValue: number, calcValue: number): string => {
    // 判断设计值/实际值/任务值和计算值是否都为 0
    if (compareValue === 0 && calcValue === 0) {
      return "0%";
    }
    // 如果计算值不为 0 但设计值/实际值/任务值为 0
    if (compareValue === 0 && calcValue !== 0) {
      return "100%";
    }
    // 正常计算误差百分比：(计算值 - 设计值/实际值/任务值) ÷ 设计值/实际值/任务值
    const errorPercent = ((calcValue - compareValue) / compareValue) * 100;
    // 非 0 但极小的误差显示 <0.01%，避免出现"0.00% 却被标红"的矛盾
    return formatErrorPercent(errorPercent);
  };

  // 判断当前值与对比值是否有差异（仅高亮有差异的对比单元格，不整行高亮）
  const valuesDiffer = (current: any, compare: any): boolean => {
    // 缺失值：- / — / null / undefined
    const curMissing = current === "-" || current === "—" || current === null || current === undefined;
    const cmpMissing = compare === "-" || compare === "—" || compare === null || compare === undefined;
    // 任一侧缺失（页面显示为 "-"/"—"）都不算差异，不标红
    if (curMissing || cmpMissing) return false;
    const curStr = String(current);
    const cmpStr = String(compare);
    const curNum = parseFloat(curStr);
    const cmpNum = parseFloat(cmpStr);
    const curIsNum = !isNaN(curNum);
    const cmpIsNum = !isNaN(cmpNum);
    if (curIsNum && cmpIsNum) {
      // 数值比较：相对容差比较，忽略浮点噪声（相对误差 < 0.0000001% 视为相等，与显示一致）
      if (curNum === cmpNum) return false;
      const tolerance = Math.max(Math.abs(curNum), Math.abs(cmpNum)) * 1e-9;
      return Math.abs(curNum - cmpNum) > tolerance;
    }
    // 非数值：字符串直接比较
    return curStr !== cmpStr;
  };

  // 获取计算值和对比值
  const getVal = (stream: any, compareData: any[], idx: number) => {
    let calc: number;
    switch (mode) {
      case "massFlow":
        calc = (stream.f_kg_per_h ?? 0) * (stream.mass_frac?.[idx] ?? 0);
        break;
      case "massFraction":
        calc = stream.mass_frac?.[idx] ?? "-";
        break;
      case "molarFlow":
        calc = ((stream.f_mol_per_h ?? 0) * (stream.molar_frac?.[idx] ?? 0)) / 1000;
        break;
      case "massConcentration":
        calc = ((stream.f_kg_per_h ?? 0) * 1e6 * (stream.mass_frac?.[idx] ?? 0)) / (stream.f_nm3_per_h || stream.f_m3_per_h || 1);
        break;
      case "moleFrac":
      default:
        calc = stream.molar_frac?.[idx] ?? 0;
        break;
    }
    const compareVals = compareData.map((cs: any) => getCompareVal(cs, idx));
    return { calc, compareVals };
  };

  // 获取带误差百分比的值（sig 为同一组分内自适应精度，数值与误差均按新规则格式化）
  const getValWithError = (stream: any, compareData: any[], idx: number, sig: number = 6) => {
    const { calc, compareVals } = getVal(stream, compareData, idx);
    const compareValsWithError = compareVals.map((val) => {
      if (val == null) return { val: null, error: "-" };
      // 误差保留原有语义：0 特判（两侧都为 0 显示 0%，计算值非 0 显示 100%）
      const errorPercent = val !== 0
        ? calculateErrorPercent(val, calc)
        : (calc === 0 ? "0%" : "100%");
      // 数值按有效数字格式化，极小非 0 值不会显示成 0
      return { val: formatBySig(val, sig), error: errorPercent };
    });
    return { calc: formatBySig(calc, sig), compareValsWithError };
  };

  const compareCount = compareItems.length;

  // ========== 行列过滤逻辑 ==========
  const matIndices = materials.map((_, i) => i);

  const getCalcVal = (stream: any, idx: number): number => {
    switch (mode) {
      case "massFlow":
        return (stream.f_kg_per_h ?? 0) * (stream.mass_frac?.[idx] ?? 0);
      case "massFraction":
        return stream.mass_frac?.[idx] ?? "-";
      case "molarFlow":
        return ((stream.f_mol_per_h ?? 0) * (stream.molar_frac?.[idx] ?? 0)) / 1000;
      case "massConcentration":
        return ((stream.f_kg_per_h ?? 0) * 1e6 * (stream.mass_frac?.[idx] ?? 0)) / (stream.f_nm3_per_h || stream.f_m3_per_h || 1);
      case "moleFrac":
      default:
        return stream.molar_frac?.[idx] ?? 0;
    }
  };

  const getCompareVal = (csInfo: any, idx: number): number | null => {
    const cs = csInfo?.stream;
    if (!cs?.materials?.[idx]) return null;
    // 保留 null（缺失组分），显示为 "-"
    const molarFrac = cs.materials[idx].molar_frac;
    switch (mode) {
      case "moleFrac":
        return molarFrac;
      case "molarFlow":
        // 使用输出的总摩尔流量 (mol/h)，乘以摩尔分率，除以 1000 转换为 kmol/h
        if (molarFrac == null) return null;
        return ((cs.f_mol_per_h ?? 0) * molarFrac) / 1000;
      case "massFraction":
        // 设计值无质量分率数据，返回 null 显示 "—"
        if (!cs.mass_frac || cs.mass_frac[idx] == null) return null;
        return cs.mass_frac[idx];
      case "massFlow":
        // 设计值无质量分率数据，返回 null 显示 "—"
        if (!cs.mass_frac || cs.mass_frac[idx] == null) return null;
        return (cs.params?.f ?? 0) * (cs.mass_frac[idx] ?? 0);
      case "massConcentration": {
        // 设计值无质量分率数据，返回 null 显示 "—"
        if (!cs.mass_frac || cs.mass_frac[idx] == null) return null;
        const volFlow = cs.f_nm3_per_h || cs.f_m3_per_h || 1;
        return ((cs.params?.f ?? 0) * (cs.mass_frac[idx] ?? 0) * 1e6) / volFlow;
      }
      default:
        return null;
    }
  };

  const isZeroOrInvalid = (val: any): boolean => {
    if (val === null || val === undefined || val === "—") return true;
    return Number(val) === 0;
  };

  const { visibleStreams, visibleMatIndices } = useMemo(() => {
    if (!streams.length) return { visibleStreams: streams, visibleMatIndices: matIndices };
    if (showAllData) return { visibleStreams: streams, visibleMatIndices: matIndices };

    const vs = streams.filter((s) => {
      return !matIndices.every((idx) => {
        const cv = getCalcVal(s, idx);
        if (!isZeroOrInvalid(cv)) return false;
        const cd = s.compareData || [];
        for (let i = 0; i < compareCount; i++) {
          const cv2 = getCompareVal(cd[i], idx);
          if (!isZeroOrInvalid(cv2)) return false;
        }
        return true;
      });
    });

    const vm = matIndices.filter((idx) => {
      return !streams.every((s) => {
        const cv = getCalcVal(s, idx);
        if (!isZeroOrInvalid(cv)) return false;
        const cd = s.compareData || [];
        for (let i = 0; i < compareCount; i++) {
          const cv2 = getCompareVal(cd[i], idx);
          if (!isZeroOrInvalid(cv2)) return false;
        }
        return true;
      });
    });

    return { visibleStreams: vs, visibleMatIndices: vm };
  }, [streams, showAllData, mode, compareCount, materials, keyComponents]);

  // 将上下文中的关键组分标签转为 materials 数组中的索引
  const materialLabels = useMemo(() => materials.map((mat: any) => rebuildFormula(mat?.name || "")), [materials]);
  const displayMatIndices = useMemo(() => {
    return materialLabels
      .map((label, i) => ({ label, i }))
      .filter(({ label }) => keyComponents.includes(label))
      .map(({ i }) => i);
  }, [keyComponents, materialLabels]);

  const handleKeyComponentChange = (checkedValues: string[]) => {
    setKeyComponents(checkedValues);
  };

  const totalColSpan = 4 + displayMatIndices.length * (1 + compareCount);

  return (
    <div className="result-component-table-container">
      <div className="mode-selector-container">
        <span className="mode-selector-label" style={{ marginLeft: 8 }}>组分数据：</span>
        <div className="mode-buttons">
          {modes.map((m) => (
            <button
              key={m.value}
              className={`mode-button ${mode === m.value ? "active" : ""}`}
              onClick={() => setMode(m.value as DataMode)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <Dropdown
          trigger={["click"]}
          overlay={
            <div style={{ padding: 12, background: "#fff", minWidth: 200 }}>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>选择关键组分：</div>
              <Checkbox.Group
                value={keyComponents}
                onChange={(checkedValues) => handleKeyComponentChange(checkedValues as string[])}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                {materials.map((mat, i) => (
                  <Checkbox key={i} value={materialLabels[i]}>
                    {replaceFormulaStr(mat?.name || `组分${i + 1}`)}
                  </Checkbox>
                ))}
              </Checkbox.Group>
              <div style={{ marginTop: 8, borderTop: "1px solid #f0f0f0", paddingTop: 8, display: "flex", gap: 12 }}>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setKeyComponents(materialLabels)}
                >
                  全选
                </Button>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setKeyComponents([])}
                >
                  取消全选
                </Button>
              </div>
            </div>
          }
        >
          <Button style={{ position: "absolute", right: "2%", display: "inline-flex", gap: 0 }}><span style={{ fontSize: 18 }}>⚙️</span>设置关键组分</Button>
        </Dropdown>
      </div>

      <div className="table-container">
        <div className="table-scroll-wrapper">
          <table className="component-data-table">
            <thead>
              <tr className="table-header">
                <th rowSpan={2}>流股</th>
                <th rowSpan={2}>流股描述</th>
                <th rowSpan={2}>源</th>
                <th rowSpan={2}>目标</th>
                {displayMatIndices.map((idx) => (
                  <th key={idx} colSpan={1 + compareCount}>{replaceFormulaStr(materials[idx]?.name)}</th>
                ))}
              </tr>
              {compareCount > 0 && (
                <tr className="table-header">
                  {displayMatIndices.map((idx) => (
                    <React.Fragment key={idx}>
                      <th>当前计算值</th>
                      {compareItems.map((item, i) => (
                        <th key={i}>
                          {item.type === "task" 
                            ? item.values.name 
                            : item.type === "otherCalc"
                            ? `${item.values.name} (${item.created_at ? dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss") : ''})`
                            : item.type === "design" ? `设计值 (${item.id})` : (item.name || item.values?.name) || (item.type === "timeRange" ? (item.fullDay ? `运行数据 ${item.date}` : `运行数据 ${item.startTime} ~ ${item.endTime}`) : `运行数据 (${item.id})`)}
                        </th>
                      ))}
                    </React.Fragment>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {visibleStreams.length > 0 ? (
                visibleStreams.map((stream, i) => {
                  const compareData = stream.compareData || [];
                  return (
                    <tr key={i} className="table-row">
                      <td>{stream.name}</td>
                      <td>{streamDescMap[stream.name] || "—"}</td>
                      <td>
                        <Tooltip title={elementDescMap[streamNodeMap[stream.name]?.fromName] || "暂无描述"}>
                          <span style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}>{streamNodeMap[stream.name]?.fromName || "—"}</span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip title={elementDescMap[streamNodeMap[stream.name]?.toName] || "暂无描述"}>
                          <span style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}>{streamNodeMap[stream.name]?.toName || "—"}</span>
                        </Tooltip>
                      </td>
                      {displayMatIndices.map((idx) => {
                        // 先收集该组分的计算值与全部对比值（原始值），用于统一精度与 title 提示
                        const { calc: rawCalc, compareVals: rawCompareVals } = getVal(stream, compareData, idx);
                        // 该组分内自适应精度：常态 6 位有效数字，有差异时提升精度，保证差异在显示上也能看出来
                        const sig = resolveSig([rawCalc, ...rawCompareVals], valuesDiffer);
                        const { calc, compareValsWithError } = getValWithError(stream, compareData, idx, sig);
                        // 该组分下任一对比值与计算值有差异，则该组分的计算值+所有对比值单元格标红
                        const hasDiff = rawCompareVals.some((val) => valuesDiffer(rawCalc, val));
                        return (
                          <React.Fragment key={idx}>
                            <td className={hasDiff ? "compare-cell-diff" : ""} title={String(rawCalc)}>{calc}</td>
                            {compareCount > 0 &&
                              compareValsWithError.map((item, j) => (
                                <td key={j} className={hasDiff ? "compare-cell-diff" : ""} title={String(rawCompareVals[j])}>
                                  {item.val != null ? `${item.val}(${item.error})` : "—"}
                                </td>
                              ))}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={totalColSpan} className="empty-cell">请选择流股</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResultComponentTable;